import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import { getStoreVisibilityLabel } from '../../context-api/api-services/StoreManagement.service';
import TextTransformation from '../../utils/textTransformation';
import { COLORS } from './storeManagementConstants';

// ── Helpers ───────────────────────────────────────────────────────────────────

export function formatLastRefresh(timestampMs) {
  if (timestampMs == null || !Number.isFinite(timestampMs)) return '—';
  try {
    return new Date(timestampMs).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  } catch (_) {
    return '—';
  }
}

function getInitials(name) {
  if (!name || name === '—') return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// ── Badge / Pill component ────────────────────────────────────────────────────

function PillBadge({ label, color, bgColor, borderColor, icon: Icon }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: bgColor,
        color,
        border: `1px solid ${borderColor || color + '33'}`,
        borderRadius: '100px',
        px: 1.25,
        py: 0.45,
        fontSize: 11.5,
        fontFamily: 'Inter Bold, sans-serif',
        fontWeight: 700,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {Icon && <Icon sx={{ fontSize: 13, color }} />}
      {label}
    </Box>
  );
}

// ── Kitchen initials avatar ───────────────────────────────────────────────────

export function KitchenAvatar({ name, size = 36, radius = '10px' }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: radius,
        bgcolor: 'rgba(63, 63, 110, 0.08)',
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Inter Bold, sans-serif',
        fontSize: size < 36 ? 11 : 12,
        fontWeight: 700,
        color: COLORS.ink,
        flexShrink: 0,
        letterSpacing: '0.02em',
      }}
    >
      {getInitials(name)}
    </Box>
  );
}

// ── Status + Visibility renderers ─────────────────────────────────────────────

export function getStatusDisplay(status_type) {
  if (status_type == null || status_type === '') return null;
  const normalized = String(status_type).toLowerCase();

  if (normalized === 'approved' || normalized === 'active') {
    return (
      <PillBadge
        label={normalized === 'approved' ? 'Approved' : TextTransformation(status_type)}
        color="#15803d"
        bgColor="#dcfce7"
        borderColor="#86efac"
        icon={CheckCircleRounded}
      />
    );
  }
  if (
    normalized === 'rejected' ||
    normalized === 'reject' ||
    normalized.includes('reject') ||
    normalized === 'inactive'
  ) {
    return (
      <PillBadge
        label={TextTransformation(status_type)}
        color="#dc2626"
        bgColor="#fee2e2"
        borderColor="#fca5a5"
        icon={CancelRounded}
      />
    );
  }
  if (normalized.includes('pending') || normalized.includes('review')) {
    return (
      <PillBadge
        label={TextTransformation(status_type)}
        color="#b45309"
        bgColor="#fef3c7"
        borderColor="#fcd34d"
        icon={WarningAmberRounded}
      />
    );
  }
  return (
    <PillBadge
      label={TextTransformation(status_type)}
      color={COLORS.muted}
      bgColor={COLORS.headerBg}
      borderColor={COLORS.border}
    />
  );
}

export function getOnboardingBoolDisplay(value) {
  if (value == null) {
    return (
      <Typography sx={{ color: COLORS.muted, fontSize: 13, fontFamily: 'Inter Regular, sans-serif' }}>
        —
      </Typography>
    );
  }
  if (value) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: '#dcfce7',
          border: '1px solid #86efac',
        }}
      >
        <CheckCircleRounded sx={{ fontSize: 16, color: '#15803d' }} />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: '#fee2e2',
        border: '1px solid #fca5a5',
      }}
    >
      <CancelRounded sx={{ fontSize: 16, color: '#dc2626' }} />
    </Box>
  );
}

export function getStoreVisibilityDisplay(canonical) {
  if (canonical == null || canonical === '') {
    return (
      <Typography sx={{ color: COLORS.muted, fontSize: 13, fontFamily: 'Inter Regular, sans-serif' }}>
        —
      </Typography>
    );
  }
  const n = String(canonical).toLowerCase();
  if (n === 'live') {
    return (
      <PillBadge
        label={getStoreVisibilityLabel('live')}
        color="#15803d"
        bgColor="#dcfce7"
        borderColor="#86efac"
        icon={CheckCircleRounded}
      />
    );
  }
  if (n === 'under_review') {
    return (
      <PillBadge
        label={getStoreVisibilityLabel('under_review')}
        color="#b45309"
        bgColor="#fef3c7"
        borderColor="#fcd34d"
        icon={WarningAmberRounded}
      />
    );
  }
  if (n === 'under_testing') {
    return (
      <PillBadge
        label={getStoreVisibilityLabel('under_testing')}
        color="#1d4ed8"
        bgColor="#dbeafe"
        borderColor="#93c5fd"
        icon={ScienceOutlined}
      />
    );
  }
  return (
    <PillBadge
      label={getStoreVisibilityLabel(canonical)}
      color={COLORS.muted}
      bgColor={COLORS.headerBg}
      borderColor={COLORS.border}
    />
  );
}
